import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Buildings, Plus, MagnifyingGlass, PencilSimple, Trash, X, CaretDown, Flask, Package, CaretLeft, CaretRight, Globe, Eye, EyeSlash, CheckSquare } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import ESignature from '../components/ESignature';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { productTypes } from '../components/study/studyConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  active: 'bg-green-50 text-green-700 border-green-200',
  inactive: 'bg-stone-50 text-stone-600 border-stone-200',
};

export default function Clients() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  const canEdit = can('clients', 'edit');
  const canDelete = can('clients', 'delete');
  const [clients, setClients] = useState([]);
  const [studies, setStudies] = useState([]);
  const [expandedClient, setExpandedClient] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', cif: '', address: '', city: '', postal_code: '', phone: '', email: '', contact_person: '', notes: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const LIMIT = 50;

  const labels = lang === 'es'
    ? { title: 'Clientes', new: 'Nuevo Cliente', edit: 'Editar Cliente', name: 'Nombre / Razon social', cif: 'CIF/NIF', address: 'Direccion', city: 'Ciudad', postal_code: 'Codigo postal', phone: 'Telefono', email: 'Email', contact_person: 'Persona de contacto', notes: 'Notas', status: 'Estado', active: 'Activo', inactive: 'Inactivo', save: 'Guardar', cancel: 'Cancelar', search: 'Buscar...', no_clients: 'No hay clientes registrados', confirm_delete: 'Eliminar este cliente?', created: 'Cliente creado', updated: 'Cliente actualizado', deleted: 'Cliente eliminado', studies: 'Estudios', samples: 'Muestras', volunteers: 'Voluntarios', no_studies: 'Sin estudios asociados', view_study: 'Ver estudio' }
    : { title: 'Clients', new: 'New Client', edit: 'Edit Client', name: 'Name / Company', cif: 'Tax ID', address: 'Address', city: 'City', postal_code: 'Postal code', phone: 'Phone', email: 'Email', contact_person: 'Contact person', notes: 'Notes', status: 'Status', active: 'Active', inactive: 'Inactive', save: 'Save', cancel: 'Cancel', search: 'Search...', no_clients: 'No clients registered', confirm_delete: 'Delete this client?', created: 'Client created', updated: 'Client updated', deleted: 'Client deleted', studies: 'Studies', samples: 'Samples', volunteers: 'Volunteers', no_studies: 'No associated studies', view_study: 'View study' };

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (search) params.append('search', search);
    Promise.all([
      axios.get(`${API_URL}/api/clients?${params}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies`, { withCredentials: true }),
    ]).then(([cr, sr]) => {
      setClients(cr.data.data || []);
      setTotalClients(cr.data.total || 0);
      setTotalPages(cr.data.pages || 1);
      setStudies(sr.data);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setPage(1); }, [search]);

  const clientStudyCount = {};
  studies.forEach(s => {
    if (s.client_id) clientStudyCount[s.client_id] = (clientStudyCount[s.client_id] || 0) + 1;
  });

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', cif: '', address: '', city: '', postal_code: '', phone: '', email: '', contact_person: '', notes: '', status: 'active' });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c.client_id);
    setForm({ name: c.name || '', cif: c.cif || '', address: c.address || '', city: c.city || '', postal_code: c.postal_code || '', phone: c.phone || '', email: c.email || '', contact_person: c.contact_person || '', notes: c.notes || '', status: c.status || 'active' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(lang === 'es' ? 'El nombre es obligatorio' : 'Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/clients/${editing}`, form, { withCredentials: true });
        toast.success(labels.updated);
      } else {
        await axios.post(`${API_URL}/api/clients`, form, { withCredentials: true });
        toast.success(labels.created);
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleDelete = async (id, signature) => {
    try {
      await axios.delete(`${API_URL}/api/clients/${id}`, { withCredentials: true, data: { signature } });
      toast.success(labels.deleted);
      setDeleteTarget(null);
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Buildings size={28} weight="duotone" className="text-[#1f3e80]" />
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-stone-900">{labels.title}</h1>
          <Badge variant="outline" className="text-xs font-mono">{totalClients}</Badge>
        </div>
        <Button data-testid="create-client-btn"
          onClick={openNew}
          disabled={!canEdit}
          title={!canEdit ? (lang === 'es' ? 'Sin permiso de edicion' : 'No edit permission') : ''}
          className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2 disabled:bg-stone-300 disabled:hover:bg-stone-300">
          <Plus size={16} />
          {labels.new}
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input data-testid="client-search" placeholder={labels.search} value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-stone-200" />
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><p className="text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p></div>
        ) : clients.length === 0 ? (
          <div className="p-12 text-center">
            <Buildings size={40} weight="duotone" className="text-stone-300 mx-auto mb-3" />
            <p className="text-sm text-stone-500">{labels.no_clients}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="w-28">ID</TableHead>
                <TableHead>{labels.name}</TableHead>
                <TableHead>{labels.cif}</TableHead>
                <TableHead>{labels.contact_person}</TableHead>
                <TableHead>{labels.phone}</TableHead>
                <TableHead className="w-24">{labels.studies}</TableHead>
                <TableHead>{labels.status}</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map(c => {
                const clientStudies = studies.filter(s => s.client_id === c.client_id);
                const isExpanded = expandedClient === c.client_id;
                return (
                  <React.Fragment key={c.client_id}>
                    <TableRow data-testid={`client-row-${c.client_id}`} className="hover:bg-stone-50">
                      <TableCell>
                        {clientStudies.length > 0 && (
                          <button data-testid={`expand-client-${c.client_id}`} onClick={() => setExpandedClient(isExpanded ? null : c.client_id)} className="p-1 rounded hover:bg-stone-100 transition-colors">
                            <CaretDown size={14} className={`text-stone-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-stone-600">{c.client_id}</TableCell>
                      <TableCell className="text-sm font-medium text-stone-900">{c.name}</TableCell>
                      <TableCell className="text-sm text-stone-600">{c.cif || '-'}</TableCell>
                      <TableCell className="text-sm text-stone-600">{c.contact_person || '-'}</TableCell>
                      <TableCell className="text-sm text-stone-600">{c.phone || '-'}</TableCell>
                      <TableCell>
                        {clientStudies.length > 0 ? (
                          <Badge variant="outline" className="text-xs font-mono bg-blue-50 text-blue-700 border-blue-200">{clientStudies.length}</Badge>
                        ) : (
                          <span className="text-xs text-stone-400">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${statusColors[c.status] || ''}`}>
                          {c.status === 'active' ? labels.active : labels.inactive}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" data-testid={`edit-client-${c.client_id}`} onClick={() => openEdit(c)} className="h-7 w-7 text-stone-500 hover:text-stone-900">
                            <PencilSimple size={14} />
                          </Button>
                          {isAdmin && (
                            <Button variant="ghost" size="icon" data-testid={`delete-client-${c.client_id}`} onClick={() => setDeleteTarget(c)} className="h-7 w-7 text-stone-400 hover:text-red-600">
                              <Trash size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && clientStudies.map(st => (
                      <TableRow key={`${c.client_id}-${st.study_id}`} data-testid={`client-study-${st.study_id}`} className="bg-stone-50/70">
                        <TableCell></TableCell>
                        <TableCell colSpan={2} className="py-2">
                          <div className="flex items-center gap-2">
                            <Flask size={14} className="text-[#1f3e80]" />
                            <button onClick={() => navigate(`/studies/${st.study_id}`)} className="text-sm font-medium text-[#1f3e80] hover:underline">{st.code}</button>
                            <span className="text-xs text-stone-500">{st.title}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-2">
                          <Badge variant="outline" className="text-[10px]">{st.status}</Badge>
                        </TableCell>
                        <TableCell className="py-2 text-xs text-stone-500">{st.product_type}</TableCell>
                        <TableCell className="py-2">
                          <div className="flex items-center gap-3 text-xs text-stone-500">
                            <span className="flex items-center gap-1"><Package size={12} />{st.sample_count || 0} {labels.samples}</span>
                            <span>{st.volunteer_count || 0} {labels.volunteers}</span>
                          </div>
                        </TableCell>
                        <TableCell colSpan={3}></TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-8 w-8 p-0" data-testid="clients-prev-page">
            <CaretLeft size={14} />
          </Button>
          <span className="text-xs text-stone-600 font-mono">
            {page} / {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-8 w-8 p-0" data-testid="clients-next-page">
            <CaretRight size={14} />
          </Button>
        </div>
      )}

      {showForm && (
        <ClientFormModal
          editing={editing}
          form={form}
          setField={setField}
          labels={labels}
          lang={lang}
          saving={saving}
          onSave={handleSave}
          onClose={() => setShowForm(false)}
        />
      )}

      <ESignature
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSigned={(signature) => handleDelete(deleteTarget.client_id, signature)}
        action={lang === 'es' ? `Eliminar cliente ${deleteTarget?.name || ''}` : `Delete client ${deleteTarget?.name || ''}`}
        resource={`client/${deleteTarget?.client_id || ''}`}
        lang={lang}
      />
    </div>
  );
}


function ClientFormModal({ editing, form, setField, labels, lang, saving, onSave, onClose }) {
  const [tab, setTab] = useState('general');
  const [portalData, setPortalData] = useState({ enabled: false, email: '', name: '', password: '', allowed_product_types: [] });
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalSaving, setPortalSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const portalLabels = lang === 'es' ? {
    general: 'Datos Generales', portal: 'Acceso Portal',
    portalEnabled: 'Acceso al portal activado', portalDisabled: 'Acceso al portal desactivado',
    enablePortal: 'Activar acceso al portal',
    portalEmail: 'Email de acceso', portalName: 'Nombre del usuario',
    portalPassword: 'Contrasena', portalPasswordHint: 'Dejar vacio para mantener la actual',
    savePortal: 'Guardar acceso', portalSaved: 'Acceso al portal actualizado',
    portalDesc: 'Configura las credenciales para que este cliente pueda acceder al portal de seguimiento de sus estudios.',
    portalUrl: 'URL del portal',
    newClient: 'Guarda el cliente primero para configurar el acceso al portal',
    allowedTypes: 'Tipos de estudio visibles',
    allowedTypesHint: 'Si no seleccionas ninguno, el cliente vera todos sus estudios',
  } : {
    general: 'General Info', portal: 'Portal Access',
    portalEnabled: 'Portal access enabled', portalDisabled: 'Portal access disabled',
    enablePortal: 'Enable portal access',
    portalEmail: 'Access email', portalName: 'User name',
    portalPassword: 'Password', portalPasswordHint: 'Leave empty to keep current',
    savePortal: 'Save access', portalSaved: 'Portal access updated',
    portalDesc: 'Configure credentials for this client to access the study tracking portal.',
    portalUrl: 'Portal URL',
    newClient: 'Save the client first to configure portal access',
    allowedTypes: 'Visible study types',
    allowedTypesHint: 'If none selected, the client will see all their studies',
  };

  useEffect(() => {
    if (editing && tab === 'portal') {
      setPortalLoading(true);
      axios.get(`${API_URL}/api/portal/access/${editing}`, { withCredentials: true })
        .then(r => setPortalData({ ...r.data, password: '', allowed_product_types: r.data.allowed_product_types || [] }))
        .catch(err => {
          toast.error(errText(err, 'Error cargando acceso portal'));
          setPortalData({ enabled: false, email: '', name: '', password: '', allowed_product_types: [] });
        })
        .finally(() => setPortalLoading(false));
    }
  }, [editing, tab]);

  const handleSavePortal = async () => {
    setPortalSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/portal/access/${editing}`, portalData, { withCredentials: true });
      setPortalData({ ...r.data, password: '', allowed_product_types: r.data.allowed_product_types || [] });
      toast.success(portalLabels.portalSaved);
      // Re-read from server to confirm persistence
      const verify = await axios.get(`${API_URL}/api/portal/access/${editing}`, { withCredentials: true });
      setPortalData({ ...verify.data, password: '', allowed_product_types: verify.data.allowed_product_types || [] });
    } catch (err) {
      toast.error(errText(err, 'Error guardando acceso portal'));
    }
    setPortalSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="client-form-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h2 className="text-lg font-heading font-semibold text-stone-900">{editing ? labels.edit : labels.new}</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8"><X size={18} /></Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-200">
          <button
            type="button"
            onClick={() => setTab('general')}
            data-testid="client-tab-general"
            className={`flex-1 py-3 px-4 text-sm font-medium text-center border-b-2 transition-colors ${
              tab === 'general' ? 'border-[#1f3e80] text-[#1f3e80]' : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}
          >
            <Buildings size={14} className="inline mr-1.5" />
            {portalLabels.general}
          </button>
          <button
            type="button"
            onClick={() => setTab('portal')}
            data-testid="client-tab-portal"
            className={`flex-1 py-3 px-4 text-sm font-medium text-center border-b-2 transition-colors ${
              tab === 'portal' ? 'border-[#1f3e80] text-[#1f3e80]' : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}
          >
            <Globe size={14} className="inline mr-1.5" />
            {portalLabels.portal}
            {portalData.enabled && <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-green-500" />}
          </button>
        </div>

        {tab === 'general' ? (
          <>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-sm font-medium text-stone-800">{labels.name} <span className="text-red-500">*</span></Label>
                  <Input data-testid="client-name-input" value={form.name} onChange={e => setField('name', e.target.value)} className="bg-white border-stone-200" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-stone-800">{labels.cif}</Label>
                  <Input data-testid="client-cif-input" value={form.cif} onChange={e => setField('cif', e.target.value)} className="bg-white border-stone-200" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-stone-800">{labels.contact_person}</Label>
                  <Input data-testid="client-contact-input" value={form.contact_person} onChange={e => setField('contact_person', e.target.value)} className="bg-white border-stone-200" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-stone-800">{labels.email}</Label>
                  <Input data-testid="client-email-input" value={form.email} onChange={e => setField('email', e.target.value)} className="bg-white border-stone-200" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-stone-800">{labels.phone}</Label>
                  <Input data-testid="client-phone-input" value={form.phone} onChange={e => setField('phone', e.target.value)} className="bg-white border-stone-200" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-sm font-medium text-stone-800">{labels.address}</Label>
                  <Input data-testid="client-address-input" value={form.address} onChange={e => setField('address', e.target.value)} className="bg-white border-stone-200" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-stone-800">{labels.city}</Label>
                  <Input data-testid="client-city-input" value={form.city} onChange={e => setField('city', e.target.value)} className="bg-white border-stone-200" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-stone-800">{labels.postal_code}</Label>
                  <Input data-testid="client-postal-input" value={form.postal_code} onChange={e => setField('postal_code', e.target.value)} className="bg-white border-stone-200" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-stone-800">{labels.status}</Label>
                  <Select value={form.status} onValueChange={v => setField('status', v)}>
                    <SelectTrigger data-testid="client-status-select" className="bg-white border-stone-200"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{labels.active}</SelectItem>
                      <SelectItem value="inactive">{labels.inactive}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{labels.notes}</Label>
                <Textarea data-testid="client-notes-input" value={form.notes} onChange={e => setField('notes', e.target.value)} className="bg-white border-stone-200 min-h-[60px]" />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-stone-200">
              <Button variant="outline" onClick={onClose} className="rounded-sm">{labels.cancel}</Button>
              <Button data-testid="save-client-btn" onClick={onSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">
                {saving ? '...' : labels.save}
              </Button>
            </div>
          </>
        ) : (
          <div className="p-6 space-y-5">
            {!editing ? (
              <div className="text-center py-8">
                <Globe size={36} className="text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">{portalLabels.newClient}</p>
              </div>
            ) : portalLoading ? (
              <div className="text-center py-8">
                <div className="h-6 w-6 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : (
              <>
                <p className="text-xs text-stone-500">{portalLabels.portalDesc}</p>

                {/* Enable/Disable toggle */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setPortalData(p => ({ ...p, enabled: !p.enabled }))}
                    data-testid="portal-access-toggle"
                    className={`relative w-11 h-6 rounded-full transition-colors ${portalData.enabled ? 'bg-green-500' : 'bg-stone-300'}`}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${portalData.enabled ? 'translate-x-5' : ''}`} />
                  </button>
                  <span className={`text-sm font-medium ${portalData.enabled ? 'text-green-700' : 'text-stone-500'}`}>
                    {portalData.enabled ? portalLabels.portalEnabled : portalLabels.portalDisabled}
                  </span>
                </div>

                {portalData.enabled && (
                  <div className="space-y-4 pt-2">
                    {/* Allowed product types */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-stone-800">{portalLabels.allowedTypes}</Label>
                      <p className="text-[11px] text-stone-400">{portalLabels.allowedTypesHint}</p>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        {productTypes.map(pt => {
                          const checked = (portalData.allowed_product_types || []).includes(pt.value);
                          return (
                            <button
                              key={pt.value}
                              type="button"
                              onClick={() => {
                                setPortalData(p => {
                                  const current = p.allowed_product_types || [];
                                  const next = checked ? current.filter(v => v !== pt.value) : [...current, pt.value];
                                  return { ...p, allowed_product_types: next };
                                });
                              }}
                              data-testid={`portal-type-${pt.value}`}
                              className={`flex items-center gap-2 p-2.5 rounded border text-left text-sm transition-colors ${
                                checked ? 'border-[#1f3e80] bg-blue-50 text-[#1f3e80] font-medium' : 'border-stone-200 text-stone-600 hover:border-stone-300'
                              }`}
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                checked ? 'bg-[#1f3e80] border-[#1f3e80]' : 'border-stone-300 bg-white'
                              }`}>
                                {checked && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                              </div>
                              {lang === 'es' ? pt.es : pt.en}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-stone-800">{portalLabels.portalEmail} <span className="text-red-500">*</span></Label>
                      <Input
                        data-testid="portal-email-input"
                        type="email"
                        value={portalData.email}
                        onChange={e => setPortalData(p => ({ ...p, email: e.target.value }))}
                        placeholder="cliente@empresa.com"
                        className="bg-white border-stone-200"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-stone-800">{portalLabels.portalName}</Label>
                      <Input
                        data-testid="portal-name-input"
                        value={portalData.name}
                        onChange={e => setPortalData(p => ({ ...p, name: e.target.value }))}
                        placeholder={form.name}
                        className="bg-white border-stone-200"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-stone-800">{portalLabels.portalPassword}</Label>
                      <div className="relative">
                        <Input
                          data-testid="portal-password-input"
                          type={showPassword ? 'text' : 'password'}
                          value={portalData.password}
                          onChange={e => setPortalData(p => ({ ...p, password: e.target.value }))}
                          className="bg-white border-stone-200 pr-10"
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                          {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <p className="text-[11px] text-stone-400">{portalLabels.portalPasswordHint}</p>
                    </div>

                    {/* Portal URL info */}
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
                      <strong>{portalLabels.portalUrl}:</strong>{' '}
                      <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-[11px]">
                        {window.location.origin}/portal/login
                      </code>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-3 border-t border-stone-200">
                  <Button variant="outline" onClick={onClose} className="rounded-sm text-xs">{labels.cancel}</Button>
                  <Button
                    data-testid="save-portal-access-btn"
                    onClick={handleSavePortal}
                    disabled={portalSaving}
                    className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm text-xs gap-1.5"
                  >
                    <Globe size={14} />
                    {portalSaving ? '...' : portalLabels.savePortal}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
